<template>
  <div>
    <Modal v-if="selectedApplication !== null">
      <h1 slot="header">
        Edit Application {{ selectedApplication.index }} :
        {{ selectedApplication.section1.firstName }}
        {{ selectedApplication.section1.lastName }}
      </h1>
      <div slot="body">
        <Form
          :application="selectedApplication"
          :onValueChanged="onApplicationValuesChanged"
          :addAdditionalSectionThree="addAdditionalSectionThree"
          :removeSectionThree="removeSectionThree"
          :addAdditionalSectionFour="addAdditionalSectionFour"
          :removeSectionFour="removeSectionFour"
          :allowImages="false"
        />
      </div>
      <div slot="footer">
        <button class="btn btn-info btn-sm mr-2" @click="() => saveEdits()">
          Save
        </button>
        <button class="btn btn-danger btn-sm" @click="() => cancelEditing()">
          Cancel
        </button>
      </div>
    </Modal>
    <Header
      :filter="filter"
      :keywords="keywords"
      :gender="gender"
      :state="state"
      :age="age"
      :militaryService="militaryService"
      :onValueChanged="onValueChanged"
      :logout="logout"
      :exportTable="exportTable"
      :exportEmails="exportEmails"
    />
    <DatabaseSelector
      class="w-100"
      :tab="tab"
      :onTabSelected="onValueChanged"
    />
    <div
      style="width: 100%; min-width: 100%; max-width: 100%; overflow-x: scroll;"
    >
      <Table
        :applications="visibleApplications"
        :onApplicationSelected="onApplicationSelected"
        :viewApplication="viewApplication"
        :editApplication="editApplication"
        :viewApplicationNotes="viewApplicationNotes"
      />
    </div>
  </div>
</template>

<script>
import DatabaseSelector from "@/components/Search/DatabaseSelector";
import Form from "@/components/Application/Form";
import Header from "@/components/Search/Header";
import Modal from "@/components/Modal";
import Table from "@/components/Search/Table";

export default {
  name: "search",
  components: {
    DatabaseSelector,
    Form,
    Header,
    Modal,
    Table
  },
  data() {
    return {
      filter: "",
      keywords: "",
      gender: "All",
      state: "All",
      age: "All",
      militaryService: "All",
      tab: "general_database",
      selectedApplication: null
    };
  },
  async mounted() {
    if (this.applications.length > 0) {
      return;
    }
    try {
      let alert = this.$swal({
        title: "Loading applications...",
        type: "info",
        showConfirmButton: false,
        showCancelButton: false,
        showCloseButton: false,
        allowEscapeKey: false,
        allowOutsideClick: false,
        allowEnterKey: false
      });
      await this.$store.dispatch("applications/loadAll");
      alert.close();
    } catch (err) {
      this.$swal({
        title: "An unexpected error occurred!",
        text: "Speakers Bureau applications we unable load!",
        type: "error"
      });
    }
  },
  computed: {
    applications() {
      return this.$store.getters["applications/all"];
    },
    visibleApplications() {
      return this.applications.filter(application => {
        if (application.type !== this.tab) {
          return false;
        }
        let filter = this.filter.toLowerCase().trim();
        if (
          filter &&
          !application.section1.firstName.toLowerCase().includes(filter) &&
          !application.section1.lastName.toLowerCase().includes(filter) &&
          !application.section1.email.toLowerCase().includes(filter) &&
          !String(application.index).includes(filter)
        ) {
          return false;
        }
        if (this.gender && this.gender !== "All") {
          const parsedGender = application.section1.gender
            .toLowerCase()
            .split(" ");
          if (this.gender === "m") {
            if (
              !parsedGender.some(e => e === "male" || e === "man" || e === "m")
            ) {
              return false;
            }
          } else if (this.gender === "f") {
            if (
              !parsedGender.some(
                e => e === "female" || e === "woman" || e === "f"
              )
            ) {
              return false;
            }
          } else {
            if (
              parsedGender.some(
                e =>
                  e === "female" ||
                  e === "woman" ||
                  e === "f" ||
                  e === "male" ||
                  e === "man" ||
                  e === "m"
              )
            ) {
              return false;
            }
          }
        }
        if (this.state !== "All" && this.state !== application.section1.state) {
          return false;
        }
        const ageRange = this.age.split("-");
        let min = parseInt(ageRange[0] || 0);
        let max = parseInt(ageRange[1] || Number.MAX_SAFE_INTEGER);
        if (ageRange[0] && !ageRange[1] && application.age > min) {
          return false;
        } else if (!ageRange[0] && ageRange[1] && application.age < max) {
          return false;
        } else if (
          ageRange[0] &&
          ageRange[1] &&
          !(application.age >= min && application.age <= max)
        ) {
          return false;
        }
        if (
          this.militaryService !== "All" &&
          this.militaryService === "true" &&
          !application.section1.militaryRadio1
        ) {
          return false;
        }
        let keywords = this.keywords
          .split(",")
          .map(keyword => keyword.trim())
          .filter(keyword => keyword.length > 0);
        if (keywords.length > 0) {
          let containsAKeyword = false;
          for (let keyword of keywords) {
            const object = application.firebaseCompatibleApplication;
            const expression = new RegExp(keyword, "gmi");
            if (this.checkAllObjectsForExpression(object, expression)) {
              return true;
            }
          }
          return false;
        }
        return true;
      });
    },
    emailList() {
      return this.visibleApplications.map(
        application => application.section1.email
      );
    },
    editApplicationModalTitle() {
      if (this.selectedApplication !== null) {
        return `Edit Application ${this.selectedApplication.index}: ${this.selectedApplication.section1.firstName} ${this.selectedApplication.section1.lastName}`;
      } else {
        return "Edit Application";
      }
    },
    csv() {
      const applications = this.visibleApplications.map(application => {
        return `"${application.index}","${application.section1.firstName}","${
          application.section1.lastName
        }","${application.section1.gender}","${
          application.section1.sexualOrientation
        }","${application.section1.city}","${application.section1.state}","${
          application.age
        }","${application.races.join(", ")}","${
          application.section1.militaryRadio1 === true ? "Yes" : "No"
        }","${application.section1.email}","${application.experienceTypes.join(
          ", "
        )}","${application.experienceAges.join(
          ", "
        )}","${application.perpetrators.join(
          ", "
        )}","${application.rapeKitsReceived.join(
          ", "
        )}","${application.reportedToPolice.join(", ")}","${
          application.submissionDate
        }"`;
      });
      return `Index,First Name,Last Name,Gender,Sexual Orientation,City,State,Age,Race/Ethnicity,Military Service,Email Address,Type of Sexual Violence Experienced,Age During Abuse,Perpetrator(s),Rape Kit Received,Reported to Police,Application Submission Date\n${applications.join(
        "\n"
      )}`;
    }
  },
  methods: {
    onValueChanged(name, value) {
      this[name] = value;
    },
    checkAllObjectsForExpression(object, expression) {
      for (let item in object) {
        if (typeof object[item] === "object") {
          if (this.checkAllObjectsForExpression(object[item], expression)) {
            return true;
          }
        }
        if (String(object[item]).match(expression)) {
          return true;
        }
      }
      return false;
    },
    async onApplicationSelected(application) {
      const inputOptions = {
        general_database: "General Database",
        selected_speakers: "Selected Speaker",
        new_applicants: "New Applicants",
        removed: "Removed",
        email_bounces: "Email Bounces",
        family_and_friends: "Friends and Family",
        rejected: "Rejected",
        hire_vue: "HireVue",
        update_needed: "Update Needed"
      };
      const { value: type } = await this.$swal({
        title: "Would you like to change this applications database?",
        input: "select",
        type: "question",
        inputOptions,
        inputPlaceholder: `Current: ${inputOptions[application.type]}`,
        showCancelButton: true
      });
      if (type) {
        try {
          await application.updateType(type);
          this.$swal({
            title: "Application updated!",
            type: "success"
          });
        } catch (err) {
          this.$swal({
            title: "Unable to update application!",
            text: "An unexpected error occurred!",
            type: "error"
          });
        }
      }
    },
    async viewApplication(application) {
      const URLs = await application.getImageURLs();
      this.$swal({
        title: "View Application",
        html: `<div style="max-height: 75vh; overflow-y: scroll;">
          <div style="width: 90%; margin-left: 5%;">
            <iframe
              style="width: 100%; height: 70vh;"
              src="${application.PDF}"
              name="Speakers Bureau Application ${application.index}"
              title="Speakers Bureau Application ${application.index}"
            >
            </iframe>
            <p>Scroll down to view provided images</p>
            <div
              id="carousel"
              class="carousel slide" data-ride="carousel"
              style="height: 500px; background-color: #333;"
            >
              <div class="carousel-inner">
                ${URLs.map((url, i) => {
                  return `<div class="carousel-item ${i === 0 ? "active" : ""}">
                    <img
                      class="d-block"
                      src="${url}"
                      style="height: 500px; margin: auto;"
                    />
                  </div>`;
                })}
              </div>
              <a
                class="carousel-control-prev"
                href="#carousel"
                role="button"
                data-slide="prev"
              >
                <span class="carousel-control-prev-icon" aria-hidden="true"></span>
                <span class="sr-only">Previous</span>
              </a>
              <a
                class="carousel-control-next"
                href="#carousel"
                role="button"
                data-slide="next"
              >
                <span class="carousel-control-next-icon" aria-hidden="true"></span>
                <span class="sr-only">Next</span>
              </a>
            </div>
          </div>
        </div>`,
        grow: "fullscreen"
      });
    },
    editApplication(application) {
      this.selectedApplication = application;
    },
    onApplicationValuesChanged(section, name, value) {
      if (this.selectedApplication !== null) {
        this.$set(this.selectedApplication[section], name, value);
      }
    },
    addAdditionalSectionThree() {
      if (this.selectedApplication !== null) {
        this.selectedApplication.addAdditionalSectionThree();
      }
    },
    removeSectionThree(index) {
      if (this.selectedApplication !== null) {
        this.selectedApplication.removeAdditionalSectionThree(index - 1);
      }
    },
    addAdditionalSectionFour() {
      if (this.selectedApplication !== null) {
        this.selectedApplication.addAdditionalSectionFour();
      }
    },
    removeSectionFour(index) {
      if (this.selectedApplication !== null) {
        this.selectedApplication.removeAdditionalSectionFour(index - 1);
      }
    },
    async saveEdits() {
      await this.selectedApplication.update();
      this.selectedApplication = null;
      this.$swal({
        title: "Application updated!",
        type: "success"
      });
    },
    async cancelEditing() {
      await this.selectedApplication.reset();
      this.selectedApplication = null;
    },
    async viewApplicationNotes(application) {
      const result = await this.$swal({
        title: "Application information about applicant",
        text:
          "This should include information about their comfort level and skills pertaining to the Speakers Bureau. This is not visible to the Applicant.",
        input: "textarea",
        inputValue: application.notes,
        type: "info",
        showCancelButton: true
      });
      if (result.dismiss !== "cancel") {
        try {
          await application.updateNotes(result.value);
          this.$swal({
            title: "Application notes updated!",
            type: "success"
          });
        } catch (err) {
          this.$swal({
            title: "Unable to update application!",
            text: "An unexpected error occurred!",
            type: "error"
          });
        }
      }
    },
    exportTable() {
      const csvData = `data:application/csv;charset=utf-8,${this.csv}`;
      const encodedUri = encodeURI(csvData);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", "Speakers Bureau Table Data.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    exportEmails() {
      const data = this.emailList.join("\n");
      const el = document.createElement("textarea");
      // Set value (string to be copied)
      el.value = data;
      // Set non-editable to avoid focus and move outside of view
      el.setAttribute("readonly", "");
      el.style = { position: "absolute", left: "-9999px" };
      document.body.appendChild(el);
      // Select text inside element
      el.select();
      // Copy text to clipboard
      document.execCommand("copy");
      // Remove temporary element
      document.body.removeChild(el);
      this.$swal({
        title: "Email List Copied!",
        type: "success"
      });
    },
    async logout() {
      try {
        await this.$store.dispatch("auth/logout");
      } catch (err) {
        // Ignore errors
      } finally {
        this.$router.push("/login");
      }
    }
  }
};
</script>
